/**
 * DL-62766 — D2 Playwright epic for the Mapping Request Step 1 "Contact person" combobox
 * (`useContactPersonStep1Fields.ts` -> `GET /api/organizations/:orgId/users`, served by
 * `styles.ts` per its file-level note on route-recognizer last-registered-wins).
 *
 * The real Step 1 "Assign supplier" dropdown returns suppliers with random faker ids
 * (`generatePortalSuppliers`, 0-1000), so a spec that needs a *known, controllable* active/
 * inactive user mix overrides the supplier list via `apiTransform({ url: '/api/suppliers', data })`
 * to return one of the reserved ids below, then `styles.ts`'s users handler resolves that id to a
 * fixed, hand-authored user set — instead of the generic 10-user `generateUsers` fallback, which
 * has no active-status filtering and no zero/one/many seam (verified: neither that route's
 * fallback branch nor the FE hook's response mapping filters on `isActive` at all).
 *
 * Ids are >90000 so they can never collide with `generatePortalSuppliers`' 0-1000 range or the
 * DL-61877 `supplierContactsMap` ids (44/45/46) in `styles.ts`.
 */
export const CONTACT_PERSON_WIZARD_ORG_IDS = {
  twoActiveUsers: 97201, // dl-62771 (Testmo 621293) — select a non-first user, assert identity
  mixedActiveInactive: 97202, // dl-62772 (Testmo 621294) — active-only filter, absence assertion
  disjointSupplierA: 97203, // dl-62773/dl-62774 (Testmo 621297/621298) — supplier A
  disjointSupplierB: 97204, // dl-62773/dl-62774 (Testmo 621297/621298) — supplier B, disjoint from A
  zeroActiveUsers: 97205, // dl-62776 (Testmo 621303) — all inactive, so the empty state is filter-produced
  searchableNames: 97206, // dl-62775 (Testmo 621302) — shared-prefix + non-matching names
  singleActiveUser: 97207, // dl-62777 (Testmo 621304) — exactly one active user
  manyActiveUsers: 97208, // dl-62777 (Testmo 621305) — 50+ active users
} as const;

type ContactPersonWizardUser = { id: string; name: string; email?: string; isActive: boolean };

export const contactPersonWizardUsersMap: Record<number, ContactPersonWizardUser[]> = {
  [CONTACT_PERSON_WIZARD_ORG_IDS.twoActiveUsers]: [
    {
      id: 'cpw-1-1',
      name: 'Elena Marchetti',
      email: 'elena.marchetti@example.com',
      isActive: true,
    },
    { id: 'cpw-1-2', name: 'Farid Haddad', email: 'farid.haddad@example.com', isActive: true },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.mixedActiveInactive]: [
    {
      id: 'cpw-2-1',
      name: 'Amara Fontaine',
      email: 'amara.fontaine@example.com',
      isActive: true,
    },
    {
      id: 'cpw-2-2',
      name: 'Baxter Holloway',
      email: 'baxter.holloway@example.com',
      isActive: true,
    },
    {
      id: 'cpw-2-3',
      name: 'Corinne Vasquez',
      email: 'corinne.vasquez@example.com',
      isActive: false,
    },
    {
      id: 'cpw-2-4',
      name: 'Desmond Okafor',
      email: 'desmond.okafor@example.com',
      isActive: false,
    },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.disjointSupplierA]: [
    {
      id: 'cpw-3a-1',
      name: 'Greta Lindqvist',
      email: 'greta.lindqvist@example.com',
      isActive: true,
    },
    {
      id: 'cpw-3a-2',
      name: 'Hugo Delacroix',
      email: 'hugo.delacroix@example.com',
      isActive: true,
    },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.disjointSupplierB]: [
    {
      id: 'cpw-3b-1',
      name: 'Ingrid Solberg',
      email: 'ingrid.solberg@example.com',
      isActive: true,
    },
    { id: 'cpw-3b-2', name: 'Jonas Petrov', email: 'jonas.petrov@example.com', isActive: true },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.zeroActiveUsers]: [
    { id: 'cpw-4-1', name: 'Kiona Ahmadi', email: 'kiona.ahmadi@example.com', isActive: false },
    { id: 'cpw-4-2', name: 'Liam Osei', email: 'liam.osei@example.com', isActive: false },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.searchableNames]: [
    {
      id: 'cpw-5-1',
      name: 'Alessandro Rinaldi',
      email: 'alessandro.rinaldi@example.com',
      isActive: true,
    },
    {
      id: 'cpw-5-2',
      name: 'Alessia Romano',
      email: 'alessia.romano@example.com',
      isActive: true,
    },
    { id: 'cpw-5-3', name: 'Beatrix Novak', email: 'beatrix.novak@example.com', isActive: true },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.singleActiveUser]: [
    {
      id: 'cpw-6-1',
      name: 'Mateus Silveira',
      email: 'mateus.silveira@example.com',
      isActive: true,
    },
  ],
  [CONTACT_PERSON_WIZARD_ORG_IDS.manyActiveUsers]: Array.from({ length: 55 }, (_, i) => ({
    id: `cpw-7-${i + 1}`,
    name: `Volume Contact ${String(i + 1).padStart(3, '0')}`,
    email: `volume.contact.${i + 1}@example.com`,
    isActive: true,
  })),
};
